import Dropzone from 'react-dropzone'

import { Avatar, Box, Button, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { SalesDocumentTemplateFormValues } from './types'

interface SalesDocumentTemplateFormProps {
  form: FormikProps<SalesDocumentTemplateFormValues>
  companyName: string
  isUpdate?: boolean
}

export const SalesDocumentTemplateForm = ({
  form,
  companyName,
  isUpdate = false,
}: SalesDocumentTemplateFormProps) => {
  const onDropFile = (files: File[]) => {
    const logoFile = files[0]

    return form.setValues({ ...form.values, logoFile })
  }

  const onDeleteFile = () => {
    return form.setValues({ ...form.values, logoFile: undefined })
  }

  const logoUrl = useUnsafeMemo(() => {
    if (form.values.logoFile) {
      return URL.createObjectURL(form.values.logoFile)
    }
  }, [form.values.logoFile])

  return (
    <Box
      gap="s16"
      overflowY="auto"
      style={{
        maxHeight: '50vh',
      }}
    >
      <TextField
        label={i18n.t('finance.sales_document_templates.modal.template_name')}
        value={form.values.name}
        onChange={(newValue) => form.setFieldValue('name', newValue)}
        error={form.touched.name ? form.errors.name : undefined}
      />
      <LegalEntitySelector
        label={i18n.t('common.legal_entity')}
        emptyLabel={i18n.t('finance.sales_document_templates.modal.legal_entity_placeholder')}
        value={form.values.legalEntity || null}
        onChange={(newValue) => {
          if (!isUpdate) {
            form.setFieldValue('legalEntity', newValue)
          }
        }}
        error={form.touched.legalEntity ? form.errors.legalEntity : undefined}
        multiple={false}
        width="full"
        disabled={isUpdate}
        hideSearchIcon
      />
      <Checkbox
        label={i18n.t('finance.sales_document_templates.modal.default_template')}
        onChange={() => form.setValues({ ...form.values, default: !form.values.default })}
        size="small"
        value="small"
        checked={form.values.default}
      />
      <Box width="full">
        <Dropzone onDrop={onDropFile} accept={{ 'image/*': [] }} multiple={false}>
          {({ getRootProps, getInputProps }) => (
            <Box width="full">
              <Box flexDirection="row" gap="s16">
                <Avatar label={companyName} url={logoUrl} size="s48" />
                <Box gap={'s12'}>
                  <Box gap={'s2'}>
                    <Text size="300" color="grey1000" weight="medium">
                      {i18n.t('finance.sales_document_templates.modal.logo.title')}
                    </Text>
                    <Text size="200" color="grey800" weight="normal">
                      {i18n.t('finance.sales_document_templates.modal.logo.description')}
                    </Text>
                  </Box>
                  <Box flexDirection="row" gap="s4">
                    <span {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Button type="secondary" iconName="camera-plus">
                        {i18n.t('finance.sales_document_templates.modal.logo.upload_logo')}
                      </Button>
                    </span>
                    {form.values.logoFile && (
                      <Button
                        type="primary"
                        iconName="delete"
                        onClick={onDeleteFile}
                        aria-label="Delete"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Dropzone>
      </Box>
      <Box width="full">
        <TextArea
          label={i18n.t('finance.sales_document_templates.modal.footer')}
          onChange={(value) => {
            if (typeof value == 'string') form.setValues({ ...form.values, footer: value })
          }}
          placeholder={i18n.t('finance.sales_document_templates.modal.footer_placeholder')}
          value={form.values.footer || ''}
        />
      </Box>
    </Box>
  )
}
